import React, { useState } from "react";
import { Link } from "react-router-dom";
import "./Privacy.css";
import LoginModal from "./LoginModal";
import logoApp from "../assets/logo-app.png";
import iconMail from "../assets/icon-mail.svg";
import iconThreads from "../assets/icon-threads.svg";
import iconFacebook from "../assets/icon-facebook.svg";
import iconInstagram from "../assets/icon-instagram.svg";

const Privacy = () => {
  const [showLoginModal, setShowLoginModal] = useState(false);

  return (
    <div className="privacy-page">
      {/* Navigation Bar */}
      <nav className="landing-nav">
        <div className="nav-container">
          <div className="nav-brand">
            <img src={logoApp} alt="Runstreak Dashboard" className="nav-logo" />
            <span className="nav-brand-text">Runstreak Dashboard</span>
          </div>
          <div className="nav-menu">
            <Link to="/" className="nav-link">Home</Link>
            <Link to="/pricing" className="nav-link">Pricing</Link>
            <Link to="/help" className="nav-link">Help</Link>
            <button onClick={() => setShowLoginModal(true)} className="nav-link nav-link-outlined">Login</button>
            <button onClick={() => setShowLoginModal(true)} className="nav-link nav-link-primary">Get Started</button>
          </div>
        </div>
      </nav>

      {/* Login Modal */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
      />

      <div className="privacy-container">
        <div className="privacy-header">
          <h1>Privacy Policy</h1>
          <p className="privacy-subtitle">
            Last updated: April 2026
          </p>
        </div>

        <section className="privacy-section">
          <h2>Introduction</h2>
          <p>
            Runstreak Dashboard ("we," "our," or "us") is committed to protecting
            your privacy. This Privacy Policy explains how we collect, use,
            disclose, and safeguard your information when you use our running
            streak tracking platform.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Information We Collect</h2>

          <h3>Account Information</h3>
          <p>When you create an account, we collect:</p>
          <ul>
            <li>Email address</li>
            <li>Username</li>
            <li>Password (encrypted and hashed — we never store plaintext)</li>
            <li>Profile information (name, profile picture)</li>
            <li>Apple ID or Google account identifier (if you sign in with those)</li>
          </ul>

          <h3>Mobile App Data</h3>
          <p>If you use the Runstreak Dashboard mobile app (iOS/Android), we additionally collect:</p>
          <ul>
            <li>Push notification tokens (to send you streak and friend alerts)</li>
            <li>App version and device OS (for debugging crash reports)</li>
          </ul>
          <p>
            The mobile app does <strong>not</strong> access your device's location,
            camera, microphone, contacts, or health data. Maps displayed in the app
            only render routes from activities you have already synced.
          </p>

          <h3>Strava Integration Data</h3>
          <p>
            If you connect your Strava account, we collect and store the following
            activity data:
          </p>
          <ul>
            <li>Activity name, date, and time</li>
            <li>Distance, duration, and pace</li>
            <li>Elevation gain and heart rate data (if available)</li>
            <li>GPS route data (polyline)</li>
            <li>Split times and pace zones</li>
            <li>Location information (geocoded from GPS coordinates)</li>
            <li>Weather conditions at the time of the activity (temperature, humidity, conditions)</li>
          </ul>

          <h3>Manually Uploaded Activities</h3>
          <p>
            For activities uploaded via GPX or TCX files, we collect the same
            activity data as Strava integration.
          </p>

          <h3>Usage Information</h3>
          <p>We automatically collect:</p>
          <ul>
            <li>Log data (IP address, browser type, access times)</li>
            <li>Device information</li>
            <li>Cookies and similar tracking technologies</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>Data Types Collected (App Store Transparency)</h2>
          <p>
            In accordance with Apple App Store and Google Play transparency
            requirements, the following table summarizes all personal data
            collected by the Runstreak Dashboard mobile app. All data is linked
            to your account, and none is used for tracking across apps or
            websites owned by third parties.
          </p>
          <ul>
            <li><strong>Email Address</strong> — account creation and login</li>
            <li><strong>Name</strong> — display on profile and to friends</li>
            <li><strong>User ID</strong> — internal account identifier</li>
            <li><strong>Precise Location</strong> — GPS routes from activities you sync (never collected live by the app)</li>
            <li><strong>Fitness</strong> — distance, pace, elevation, heart rate, cadence from your activities</li>
            <li><strong>Photos</strong> — profile picture you choose to upload</li>
          </ul>
          <p>
            <strong>We do not collect:</strong> your device's current location,
            contacts, browsing history, search history, health data, financial
            information, or advertising identifiers. We do not track you across
            other apps or websites.
          </p>
        </section>

        <section className="privacy-section">
          <h2>How We Use Your Information</h2>
          <p>We use your information to:</p>
          <ul>
            <li>Provide and maintain our service</li>
            <li>Calculate and display your running streaks</li>
            <li>Generate analytics and insights about your running activities</li>
            <li>Enable social features (friends, leaderboards)</li>
            <li>Send important service notifications</li>
            <li>Improve and optimize our platform</li>
            <li>Respond to your support requests</li>
            <li>Detect and prevent fraud or abuse</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>Data Sharing and Disclosure</h2>

          <h3>We Do Not Sell Your Data</h3>
          <p>
            We will never sell, rent, or trade your personal information to third
            parties for marketing purposes.
          </p>

          <h3>Service Providers</h3>
          <p>We may share your information with trusted service providers who assist us in operating our platform, including:</p>
          <ul>
            <li>Cloud hosting: Railway (backend and database) and Cloudflare Pages (web frontend)</li>
            <li>Authentication: Strava OAuth, Sign in with Apple, Sign in with Google</li>
            <li>Email delivery: SMTP provider for account verification and notifications</li>
            <li>Weather data: Open-Meteo API (no personal data sent — coordinates only)</li>
            <li>Profile picture storage: Azure Blob Storage</li>
            <li>Analytics: Google Analytics (anonymized web usage only — not used in the mobile app)</li>
          </ul>

          <h3>Legal Requirements</h3>
          <p>
            We may disclose your information if required by law or in response to
            valid legal requests by public authorities.
          </p>

          <h3>Social Features</h3>
          <p>
            If you use our social features (friends, leaderboards), some of your
            information (username, streak data, activity summaries) will be visible
            to other users you connect with.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Data Security</h2>
          <p>
            We implement industry-standard security measures to protect your
            information, including:
          </p>
          <ul>
            <li>Encrypted data transmission (HTTPS/SSL)</li>
            <li>Password hashing and encryption</li>
            <li>Secure cloud infrastructure</li>
            <li>Regular security audits</li>
          </ul>
          <p>
            However, no method of transmission over the Internet is 100% secure.
            While we strive to protect your data, we cannot guarantee absolute
            security.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Your Privacy Rights</h2>

          <h3>Access and Update</h3>
          <p>
            You can access and update your personal information at any time through
            your profile settings.
          </p>

          <h3>Data Export</h3>
          <p>
            You can export your activity data in CSV format from the Activities
            page.
          </p>

          <h3>Account Deletion</h3>
          <p>
            You can delete your account and all associated data at any time from
            your profile settings. This action is permanent and cannot be undone.
          </p>

          <h3>Strava Disconnection</h3>
          <p>
            You can disconnect your Strava account at any time. This will stop
            automatic syncing, but your existing data will remain unless you delete
            your account.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Cookies and Tracking</h2>
          <p>
            We use cookies and similar technologies to maintain your session,
            remember your preferences, and analyze site usage. You can control
            cookie settings through your browser preferences.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Third-Party Services</h2>

          <h3>Strava</h3>
          <p>
            Our Strava integration uses OAuth authentication. We do not have access
            to your Strava password. Please review{" "}
            <a
              href="https://www.strava.com/legal/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Strava's Privacy Policy
            </a>{" "}
            for information about how they handle your data.
          </p>

          <h3>OpenStreetMap Nominatim</h3>
          <p>
            We use OpenStreetMap's Nominatim service for reverse geocoding (converting
            GPS coordinates to location names). This service is subject to their{" "}
            <a
              href="https://osmfoundation.org/wiki/Privacy_Policy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Children's Privacy</h2>
          <p>
            Our service is not intended for children under 13 years of age. We do
            not knowingly collect personal information from children under 13. If
            you become aware that a child has provided us with personal information,
            please contact us.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you
            of any changes by posting the new Privacy Policy on this page and
            updating the "Last updated" date.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy, please contact us:
          </p>
          <p>
            <a href="mailto:hello@runstreakdashboard.com">
              hello@runstreakdashboard.com
            </a>
          </p>
        </section>
      </div>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-content">
          <div className="footer-column">
            <div className="footer-brand">
              <img src={logoApp} alt="Runstreak Dashboard" className="brand-logo" />
              <span>Runstreak Dashboard</span>
            </div>
            <p>Keep your running streak alive with the most motivational fitness app.</p>
            <div className="social-links">
              <a href="#threads"><img src={iconThreads} alt="Threads" /></a>
              <a href="#facebook"><img src={iconFacebook} alt="Facebook" /></a>
              <a href="#instagram"><img src={iconInstagram} alt="Instagram" /></a>
            </div>
          </div>

          <div className="footer-column">
            <h4>Product</h4>
            <ul>
              <li><a href="/#features">Features</a></li>
              <li><Link to="/pricing">Pricing</Link></li>
              <li><a href="#download">Download</a></li>
              <li><a href="#updates">Updates</a></li>
            </ul>
          </div>

          <div className="footer-column">
            <h4>Support</h4>
            <ul>
              <li><Link to="/help">Help Center</Link></li>
              <li><a href="mailto:hello@runstreakdashboard.com">Contact Us</a></li>
              <li><a href="mailto:hello@runstreakdashboard.com">Bug Reports</a></li>
              <li><a href="mailto:hello@runstreakdashboard.com">Feature Requests</a></li>
            </ul>
          </div>

          <div className="footer-column">
            <h4>Company</h4>
            <ul>
              <li><Link to="/about">About</Link></li>
              <li><Link to="/privacy">Privacy Policy</Link></li>
              <li><Link to="/terms">Terms of Service</Link></li>
              <li><a href="mailto:hello@runstreakdashboard.com">Careers</a></li>
            </ul>
          </div>
        </div>

        <div className="footer-divider"></div>

        <div className="footer-bottom">
          <p>© 2026 Runstreak. All rights reserved.</p>
          <div className="footer-contact">
            <img src={iconMail} alt="" />
            <a href="mailto:hello@runstreakdashboard.com">hello@runstreakdashboard.com</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Privacy;
